import Link from 'next/link';
import { type TFunction, useTranslation } from 'next-i18next';
import type { ComponentProps } from 'react';
import React from 'react';

import { BreadCrumb } from '@/components/BreadCrumb/BreadCrumb';
import { BreadCrumbSp } from '@/components/BreadCrumbSp';
import { Pager } from '@/components/Pager/Pager';
import { PageTitle } from '@/components/PageTitle';
import { PageTitleSp } from '@/components/PageTitleSp';
import type { News } from '@/pages/news';

import classNames from './NewsPage.module.scss';

const NewsEmpty: React.FC<{ t: TFunction }> = ({ t }) => (
  <p className={classNames['news-empty']}>{t('現在お知らせはありません')}</p>
);

export const NewsPage: React.FC<{
  newsList: News[];
  page: {
    current: number;
    last: number;
  };
}> = ({ newsList, page }) => {
  const { t } = useTranslation('news');
  const isFirstPage = page.current <= 1;
  const isLastPage = page.current >= page.last;

  return (
    <div className={classNames.root}>
      <PageTitleSp
        {...{ main: t('pageTitle.main'), sub: t('pageTitle.sub') }}
      />
      <BreadCrumbSp />
      <PageTitle {...{ main: t('pageTitle.main'), sub: t('pageTitle.sub') }} />
      <BreadCrumb />
      <h1 className={classNames.heading}>
        {t('お知らせ一覧')}
        <svg className={classNames['heading-decoration']}>
          <linearGradient
            id="heading-decoration-linear-gradient"
            spreadMethod="pad"
            x1="0.057"
            x2="1.042"
            y1="0.5"
            y2="0.349"
          >
            <stop offset={0} stopColor="#007fd3" stopOpacity={1} />
            <stop offset="0.7291" stopColor="#5cbcfc" stopOpacity="0.271" />
            <stop offset={1} stopColor="#007fd3" stopOpacity={0} />
          </linearGradient>
          <rect
            className={classNames['heading-decoration-rect']}
            rx={0}
            ry={0}
            x={0}
            y={0}
            width={132}
            height={4}
          />
        </svg>
      </h1>
      <div className={classNames['news-area']}>
        {newsList.length === 0 ? (
          <NewsEmpty t={t} />
        ) : (
          <>
            {newsList.map((news) => {
              const date = new Date(news.publishedAt || news.createdAt);
              const postDate = {
                year: date.getFullYear().toString().padStart(2, '0'),
                month: (date.getMonth() + 1).toString().padStart(2, '0'),
                date: date.getDate().toString().padStart(2, '0'),
              };
              return (
                <article key={news.id} className={classNames.news}>
                  <h2 className={classNames['news-title']}>
                    <Link href={`/news/${news.id}`}>{news.title}</Link>
                  </h2>
                  <time
                    dateTime={news.publishedAt || news.createdAt}
                    className={classNames['news-post-date']}
                  >{`${postDate.year}.${postDate.month}.${postDate.date}`}</time>
                  {news.category && (
                    <p className={classNames['news-category']}>
                      <span>{news.category.name}</span>
                    </p>
                  )}
                </article>
              );
            })}

            <Pager
              className={classNames.pager}
              prev={{
                href: isFirstPage
                  ? undefined
                  : `/news?page=${page.current - 1}`,
              }}
              // 関数宣言で作成した関数は hoisting が起こるので定義より前で呼び出しても問題ない
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              numbers={resolvePagerNumbers({
                currentPage: page.current,
                totalPages: page.last,
                numPagesToShow: 5,
              })}
              next={{
                href: isLastPage ? undefined : `/news?page=${page.current + 1}`,
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

// 表示するページ番号を計算するロジックは Pager 側に置くようにしたい
function resolvePagerNumbers({
  currentPage,
  totalPages,
  numPagesToShow,
}: {
  currentPage: number;
  totalPages: number;
  numPagesToShow: number;
}): ComponentProps<typeof Pager>['numbers'] {
  let startPage = Math.max(1, currentPage - Math.floor(numPagesToShow / 2));
  let endPage = Math.min(
    totalPages,
    currentPage + Math.floor(numPagesToShow / 2)
  );

  if (endPage - startPage + 1 < numPagesToShow) {
    if (startPage === 1) {
      endPage = Math.min(totalPages, startPage + numPagesToShow - 1);
    } else if (endPage === totalPages) {
      startPage = Math.max(1, endPage - numPagesToShow + 1);
    }
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
    const value = startPage + i;
    return {
      value,
      href: `/news?page=${value}`,
      active: currentPage === value,
    };
  });
}
